import { Component } from '@angular/core';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { Zone } from '../_types/zone';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ZonesService } from '../_services/zones.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { firstValueFrom } from 'rxjs';
import { ZoneLine } from '../_types/zone-line';
import { ZonesLinesService } from '../_services/zones-lines.service';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { DialogService } from 'primeng/dynamicdialog';
import { ZonesLineComponent } from './zones-line/zones-line.component';
import { ZonesUploadExcelComponent } from './zones-upload-excel/zones-upload-excel.component';
import { CountryRegion } from '../../common/_types/country-region';
import { CountriesRegionsService } from '../../common/_services/countries-regions.service';
import { CountriesService } from '../../common/_services/countries.service';
import { Country } from '../../common/_types/country';
import { LoginService } from '../../login/login.service';

@Component({
    selector: 'app-zones-detail',
    templateUrl: './zones-detail.component.html',
    styleUrls: ['./zones-detail.component.scss'],
    standalone: false
})
export class ZonesDetailComponent {
  public zoneFormGroup!: RxFormGroup

  public zone: Zone = new Zone();
  public zoneLines: ZoneLine[] = [];
  public countries: Country[] = [];
  public countriesRegions: CountryRegion[] = [];

  public loadingZoneLines: boolean = false;
  private lastGridEvent: any;
  public totalRecords: number = 0;

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private zonesService: ZonesService,
    private zonesLinesService: ZonesLinesService,
    private router: Router,
    private messageService: MessageService,
    private topbarService: TopbarService,
    private confirmationService: ConfirmationService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private dialogService: DialogService,
    private countriesService: CountriesService,
    private countriesRegionsService: CountriesRegionsService,
    private loginService: LoginService
  ) {
    this.zoneFormGroup = this.formBuilder.formGroup<Zone>(this.zone) as RxFormGroup;
  }

  ngOnInit(): void {
    this.topbarService.setTitle("Detalles de zona");
    this.topbarService.addBreadcrumb("Zonas", "/zones");
    
    this.route.paramMap.subscribe((params: ParamMap) => {
      let id: string | null = params.get('id');
      if (id != null && id != '0') {
        this.loadZone(id);
      }
    });
    this.allowWrite = this.loginService.hasPermission("ZONES.WRITE");
    this.allowDelete = this.loginService.hasPermission("ZONES.DELETE");

    this.loadCountries();
  }

  async loadZone(id: string) {
    this.zone = await firstValueFrom(this.zonesService.get(id));
    this.zoneFormGroup.patchValue(this.zone);
    if(this.zone.countryId) this.loadCountryRegions(this.zone.countryId);
  }

  async loadCountries() {
    let params: IParamsCollection = {
      page: {
        size: 1000
      },
      sort: [ 'name' ]
    }
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;

    //si no tiene pais, por defecto españa
    if(this.zone.countryId == null){
      let country = this.countries.find(x => x.code == 'ES');
      if(country){
        this.zone.countryId = country.id;
        this.zoneFormGroup.patchValue(this.zone);
        this.loadCountryRegions(country.id);
      }
    }
  }

  onCountryChange(event: any) {
    let countryId = event.value;
    this.loadCountryRegions(countryId);
  }

  async loadCountryRegions(countryId: string | null = null) {
    let params: IParamsCollection = {
      page: {
        size: 1000
      },
      sort: [ 'name' ]
    }
    if(countryId){
      let filters: any = {
        countryId: new Filter(FilterOperation.equals, "countryId", '', countryId)
      };
      params.filter = FilterBuilder.fromObject(filters).build();
    }
    let response = await firstValueFrom(this.countriesRegionsService.all(params));
    this.countriesRegions = response.data;
  }

  async loadZoneLines($event:any) {
    this.loadingZoneLines = true;
    this.lastGridEvent = $event;
    let params: IParamsCollection = {
      page: { number: $event.first + 1, size: $event.rows },
      sort: [ 'value' ]
    }
    let filters: any = {
      zoneId: new Filter(FilterOperation.equals, "zoneId"),
    };

    filters.zoneId.value = this.zone.id;
    params.filter = FilterBuilder.fromObject(filters).build();
    this.loadingZoneLines = false;

    let response = await firstValueFrom(this.zonesLinesService.all(params));
    this.zoneLines = response.data;
    this.totalRecords = response.meta['total'];
  }

  async save() {
    let valid = this.formValidationService.validateForm(this.zoneFormGroup);
    if (valid) {
      this.zone = Object.assign(this.zone, this.zoneFormGroup.value);

      await firstValueFrom(this.zonesService.save(this.zone));

      this.messageService.add({ closable: true, severity: 'success', summary: "Zona guardada correctamente." });
      
    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  async delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar la zona?",
      header: "Eliminar zona",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.zonesService.delete(this.zone.id).subscribe({
          next: (data: any) => {
            this.cancel();
            this.messageService.add({ closable: true, severity: 'success', detail: "Zona eliminada correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  cancel() {
    this.router.navigate(['zones']);
  }

  async editZoneLine(zoneLine: ZoneLine | null) {
    let ref = this.dialogService.open(ZonesLineComponent, {
      closable: true,
      modal: true,
      header: 'Ficha de línea de zona',
      width: '50%',
      data: {
        zoneId: this.zone.id,
        zoneLineId: zoneLine ? zoneLine.id : null
      }
    });

    ref.onClose.subscribe((data: any) => {
      this.loadZoneLines(this.lastGridEvent);
    });
  }

  async deleteZoneLine(id: string) {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar la línea de zona?",
      header: "Eliminar línea",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.zonesLinesService.delete(id).subscribe({
          next: (data: any) => {
            this.messageService.add({ closable: true, severity: 'success', detail: "Línea eliminada correctamente." });
            this.loadZoneLines(this.lastGridEvent);
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  loadExcel() {
    const ref = this.dialogService.open(ZonesUploadExcelComponent, {
      closable: true,
      modal: true,
      data: {
        zoneId: this.zone.id
      },
      header: "Importar Excel",
      width: '600px'
    });
    ref.onClose.subscribe((data: any) => {
      this.lastGridEvent.first = 0;
      this.loadZoneLines(this.lastGridEvent);
    });

  }

  downloadExcel() {
    // Hacer la petición al backend para obtener el archivo Excel
    let filename = this.zone.name;
    this.zonesService.downloadExcel(this.zone.id!, filename);
  }
}
